/**
 * Recently Viewed Tabs Controller
 * - Toggles between default slider content and recently viewed products
 * - Manages visibility of swipers, arrows, and underline state on tab buttons
 */

const TAB_SELECTOR = "[data-tab]";
const RECENTLY_VIEWED_TAB_SELECTOR = "[recently-viewed-tab]";
const RECENTLY_VIEWED_SECTION_SELECTOR = "[data-recently-viewed-section]";
const RECENTLY_VIEWED_ARROWS_SELECTOR = "[recently-viewed-arrows]";

class RecentlyViewedTabs {
  constructor(section) {
    this.section = section;


    this.tabs = Array.from(section.querySelectorAll(TAB_SELECTOR));
    this.recentlyViewedSection = section.querySelector(RECENTLY_VIEWED_SECTION_SELECTOR);
    this.recentlyViewedArrows = section.querySelector(RECENTLY_VIEWED_ARROWS_SELECTOR);
    this.recentlyViewedTab = section.querySelector(RECENTLY_VIEWED_TAB_SELECTOR);

    // Find default swiper/section - try multiple selectors
    this.defaultSwiperContainer = section.querySelector("[data-default-section]") ||
                                  section.querySelector(".swiper-container-wrapper:not([data-recently-viewed-section] *)");

    // Keep looking for swiper element for future use
    const allSwipers = Array.from(section.querySelectorAll("swiper-container"));

    this.defaultSwiper = allSwipers.find((swiper) => {
      const hasAttribute = swiper.hasAttribute("data-recently-viewed-swiper");
      const closestRV = swiper.closest("[data-recently-viewed-section]");
      return !hasAttribute && !closestRV;
    });

    // Find recently viewed swipers
    this.recentlyViewedSwipers = allSwipers.filter((swiper) => {
      const hasAttribute = swiper.hasAttribute("data-recently-viewed-swiper");
      const inRecentlyViewed = swiper.closest("[data-recently-viewed-section]");
      return hasAttribute || inRecentlyViewed;
    });

    // Find default arrows - with attribute [swiper-content-arrows]
    this.defaultArrows = section.querySelector("[swiper-content-arrows]");

    // Find recommended products section
    this.recommendedProductsSection = section.querySelector(".recommended-products-section");

    // Find CTA element
    this.ctaElement = section.querySelector(".cta") || section.querySelector(".cta-card");

    if (!this.tabs.length || !this.recentlyViewedSection) {
      console.warn("RecentlyViewedTabs: Missing required elements, aborting init");
      return;
    }

    this.bindEvents();
    this.activateTab("0"); // Default to first tab (default swiper)
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const tabId = tab.dataset.tab;
        this.activateTab(tabId);
      });
    });
  }

  activateTab(tabId) {
    // Toggle underline class on tab buttons
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabId;
      tab.classList.toggle("underline", isActive);
      tab.classList.toggle("regular", !isActive);
    });

    const isRecentlyViewed = tabId === "1";

    // Toggle default swiper container visibility
    if (this.defaultSwiperContainer) {
      this.defaultSwiperContainer.classList.toggle("hidden", isRecentlyViewed);
    } else {
      const fallbackContainer = this.section.querySelector(".swiper-container-wrapper:not([data-recently-viewed-section] *)");
      if (fallbackContainer) {
        fallbackContainer.classList.toggle("hidden", isRecentlyViewed);
      }
    }

    // Also toggle swiper element if found
    if (this.defaultSwiper) {
      this.defaultSwiper.classList.toggle("hidden", isRecentlyViewed);
    }

    // Toggle default arrows visibility
    if (this.defaultArrows) {
      this.defaultArrows.classList.toggle("hidden", isRecentlyViewed);
    } else {
      const fallbackArrows = this.section.querySelector(".swiper-navigation-arrows:not([recently-viewed-arrows])");
      if (fallbackArrows) {
        fallbackArrows.classList.toggle("hidden", isRecentlyViewed);
      }
    }

    // Toggle recently viewed section visibility
    if (this.recentlyViewedSection) {
      this.recentlyViewedSection.classList.toggle("hidden", !isRecentlyViewed);

      // If showing recently viewed, ensure swipers are visible
      if (isRecentlyViewed) {
        this.recentlyViewedSwipers.forEach((swiper) => {
          swiper.classList.remove('swiper-loading');
          swiper.style.opacity = '1';
          swiper.style.visibility = 'visible';
        });
      }
    }

    // Toggle recently viewed arrows visibility
    if (this.recentlyViewedArrows) {
      this.recentlyViewedArrows.classList.toggle("hidden", !isRecentlyViewed);
    }

    // Toggle recommended products section visibility
    if (this.recommendedProductsSection) {
      this.recommendedProductsSection.classList.toggle("hidden", isRecentlyViewed);
    }

    // Toggle CTA visibility
    if (this.ctaElement) {
      this.ctaElement.style.display = isRecentlyViewed ? 'none' : '';
    }

    // Show/hide recently viewed tab button
    if (this.recentlyViewedTab && isRecentlyViewed) {
      this.recentlyViewedTab.classList.remove("hidden");
    }
  }
}

const initRecentlyViewedTabs = () => {
  const sections = document.querySelectorAll("[data-recently-viewed-section]");

  sections.forEach((recentlyViewedSection) => {
    const section = recentlyViewedSection.closest("section, .shopify-section");
    if (section) {
      new RecentlyViewedTabs(section);
    }
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecentlyViewedTabs);
} else {
  initRecentlyViewedTabs();
}

export { RecentlyViewedTabs };
